/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  DrmType,
  PlayerErrorSeverity,
  PlayerState,
  StreamFormat
} from "../enums";
import type { TPlayerError } from "../types";

/**
 * Shaka Player specific helpers that complement {@link SHAKA_PLAYER_CONFIG}.
 *
 * Ported from kaveri-web-app `src/core/videoplayer/utils/ShakaPlayerUtils.ts`
 * with additional DRM mapping and format inference helpers for the unity-web
 * player layer.
 *
 * `ShakaPlayerConfig` already owns the static configuration object applied to
 * every Shaka instance — it must NOT be duplicated here. This file provides
 * functional helpers (DRM key-system mapping, stream-format inference,
 * Shaka error normalisation, state-history mapping, error classification)
 * that the player layer, the stats utility, and error mappers all need.
 *
 * No Shaka import is required: every helper operates on plain primitives or
 * structurally-typed inputs to keep the module test-friendly and free of
 * the heavy `shaka-player` runtime dependency.
 */

const FAIRPLAY_KEY_SYSTEM = "com.apple.fps";
const FAIRPLAY_LEGACY_KEY_SYSTEM = "com.apple.fps.1_0";
const WIDEVINE_KEY_SYSTEM = "com.widevine.alpha";
const PLAYREADY_KEY_SYSTEM = "com.microsoft.playready";

/**
 * Constant for converting Shaka error codes to sub-error codes.
 * Shaka error codes are in the form categoryCode * 1000 + errorOffset.
 */
const ERROR_CONVERSION_CONSTANT = 1000;

/**
 * Well-known Shaka error codes used for classification.
 * Sourced from shaka.util.Error.Code (Shaka Player 4.7.0).
 */
const ShakaErrorCode = {
  BAD_HTTP_STATUS: 1001,
  HTTP_ERROR: 1002,
  TIMEOUT: 1003,
  LOAD_INTERRUPTED: 7000,
  OPERATION_ABORTED: 7001,
  CONTENT_NOT_LOADED: 7002,
  VIDEO_ERROR: 3016,
  LICENSE_REQUEST_FAILED: 6007,
  LICENSE_RESPONSE_REJECTED: 6008
} as const;

/**
 * Well-known Shaka error categories.
 * Sourced from shaka.util.Error.Category (Shaka Player 4.7.0).
 */
const ShakaErrorCategory = {
  NETWORK: 1,
  TEXT: 2,
  MEDIA: 3,
  MANIFEST: 4,
  STREAMING: 5,
  DRM: 6,
  PLAYER: 7,
  CAST: 8,
  STORAGE: 9,
  ADS: 10
} as const;

/**
 * Mapping from Shaka's lowercase state-history strings to the application's
 * {@link PlayerState} enum. Shaka emits 'playing' | 'paused' | 'buffering' |
 * 'ended' as the recognised state-history values.
 */
const SHAKA_STATE_TO_PLAYER_STATE: ReadonlyMap<string, PlayerState> = new Map<
  string,
  PlayerState
>([
  ["playing", PlayerState.PLAYING],
  ["paused", PlayerState.PAUSED],
  ["buffering", PlayerState.BUFFERING],
  ["ended", PlayerState.ENDED]
]);

/**
 * Structural shape of a Shaka error. Uses a structural type rather than
 * `shaka.util.Error` to keep this module decoupled from the runtime.
 */
interface IShakaErrorLike {
  category?: number | string;
  code?: number | string;
  data?: ReadonlyArray<unknown>;
  severity?: number;
  message?: string;
}

export class ShakaPlayerUtils {
  // ── DRM key-system mapping ─────────────────────────────────────────────────

  /**
   * Returns the EME key system identifier for a given DRM type. Returns
   * an empty string for {@link DrmType.Clear}.
   */
  public static getKeySystem(drmType: DrmType): string {
    switch (drmType) {
      case DrmType.Widevine:
        return WIDEVINE_KEY_SYSTEM;
      case DrmType.FairPlay:
        return FAIRPLAY_KEY_SYSTEM;
      case DrmType.Clear:
      default:
        return "";
    }
  }

  /**
   * Map an EME key system identifier back to the application's DRM type.
   * Unrecognised systems resolve to {@link DrmType.Clear}.
   */
  public static getDrmTypeFromKeySystem(keySystem: string): DrmType {
    if (keySystem === WIDEVINE_KEY_SYSTEM) {
      return DrmType.Widevine;
    }
    if (
      keySystem === FAIRPLAY_KEY_SYSTEM ||
      keySystem === FAIRPLAY_LEGACY_KEY_SYSTEM
    ) {
      return DrmType.FairPlay;
    }
    return DrmType.Clear;
  }

  /**
   * Returns true for any DRM key system Shaka can negotiate today.
   */
  public static isSupportedKeySystem(keySystem: string): boolean {
    return (
      keySystem === WIDEVINE_KEY_SYSTEM ||
      keySystem === FAIRPLAY_KEY_SYSTEM ||
      keySystem === FAIRPLAY_LEGACY_KEY_SYSTEM ||
      keySystem === PLAYREADY_KEY_SYSTEM
    );
  }

  // ── Stream format inference ────────────────────────────────────────────────

  /**
   * Infer the stream format from a manifest URL. Returns `undefined` when
   * the extension is unrecognised so callers can fall back to Shaka's own
   * MIME-based detection.
   */
  public static inferStreamFormat(url: string): StreamFormat | undefined {
    if (typeof url !== "string" || url.length === 0) {
      return undefined;
    }
    // Strip query/hash so signed URLs (e.g. `…/master.m3u8?token=…`) work.
    const questionMark = url.indexOf("?");
    const hash = url.indexOf("#");
    let endIndex = url.length;
    if (questionMark !== -1) {
      endIndex = questionMark;
    }
    if (hash !== -1 && hash < endIndex) {
      endIndex = hash;
    }
    const path = url.substring(0, endIndex).toLowerCase();
    if (path.endsWith(".m3u8")) {
      return StreamFormat.HLS;
    }
    if (path.endsWith(".mpd")) {
      return StreamFormat.DASH;
    }
    return undefined;
  }

  // ── Error normalisation ────────────────────────────────────────────────────

  /**
   * Normalise a Shaka error into the application-level {@link TPlayerError}
   * shape. Tolerates partially-formed errors so it can be used both inside
   * the player and from defensive code paths.
   */
  public static toPlayerError(
    error: IShakaErrorLike | undefined
  ): TPlayerError {
    const category =
      error === undefined || error.category === undefined
        ? "UNKNOWN"
        : String(error.category);
    const code =
      error === undefined || error.code === undefined
        ? "0"
        : String(error.code);
    const message =
      error === undefined || error.message === undefined
        ? `Shaka error ${category}-${code}`
        : error.message;
    return {
      category,
      code,
      data: error === undefined ? undefined : error.data,
      message,
      severity: ShakaPlayerUtils.toPlayerErrorSeverity(
        error === undefined ? undefined : error.severity
      )
    };
  }

  /**
   * Map Shaka's numeric severity (1=RECOVERABLE, 2=CRITICAL) to the
   * application's enum. Unknown values are treated as critical to fail safe.
   */
  public static toPlayerErrorSeverity(
    severity: number | undefined
  ): PlayerErrorSeverity {
    if (severity === 1) {
      return PlayerErrorSeverity.RECOVERABLE;
    }
    return PlayerErrorSeverity.CRITICAL;
  }

  // ── Shaka error classification (ported from kaveri) ────────────────────────

  /**
   * Extract the HTTP status code from a DRM LICENSE_REQUEST_FAILED error.
   * Returns an empty string when the error is not a license request failure
   * or the HTTP code is not one of the actionable codes (401, 403, 409).
   *
   * Ported from kaveri `ShakaPlayerUtils.getHttpErrorCode`.
   */
  public static getHttpErrorCode(error: IShakaErrorLike): string {
    const errorCode =
      typeof error.code === "number" ? error.code : Number(error.code);
    if (errorCode !== ShakaErrorCode.LICENSE_REQUEST_FAILED) {
      return "";
    }
    if (!Array.isArray(error.data) || error.data.length === 0) {
      return "";
    }
    const nestedError = error.data[0];
    if (
      nestedError === null ||
      nestedError === undefined ||
      typeof nestedError !== "object"
    ) {
      return "";
    }
    const nested = nestedError as Record<string, unknown>;
    const nestedData = nested.data;
    if (!Array.isArray(nestedData) || nestedData.length < 2) {
      return "";
    }
    const httpCode = String(nestedData[1]);
    if (httpCode === "401" || httpCode === "403" || httpCode === "409") {
      return httpCode;
    }
    return "";
  }

  /**
   * Extract the {@link shaka.net.NetworkingEngine.RequestType} of the failed
   * request from a NETWORK-category error, needed to tell a manifest fetch
   * apart from a segment or license fetch when resolving the web V-code
   * (see `getWebErrorCode` in `WebPlayerErrorCodes`). The request type lives
   * at a different `error.data` index per code:
   * BAD_HTTP_STATUS → data[4], HTTP_ERROR → data[2], TIMEOUT → data[1].
   * Returns `undefined` for any other code, or when the request type was not
   * provided.
   */
  public static getRequestType(error: IShakaErrorLike): number | undefined {
    const errorCode =
      typeof error.code === "number" ? error.code : Number(error.code);
    if (!Array.isArray(error.data)) return undefined;

    let index: number;
    switch (errorCode) {
      case ShakaErrorCode.BAD_HTTP_STATUS:
        index = 4;
        break;
      case ShakaErrorCode.HTTP_ERROR:
        index = 2;
        break;
      case ShakaErrorCode.TIMEOUT:
        index = 1;
        break;
      default:
        return undefined;
    }
    const value = error.data[index];
    return typeof value === "number" ? value : undefined;
  }

  /**
   * Extract the native `HTMLMediaElement.error.code` (1=ABORTED,
   * 2=NETWORK, 3=DECODE, 4=SRC_NOT_SUPPORTED) Shaka forwards as
   * `error.data[0]` on a VIDEO_ERROR (3016), needed to tell apart the
   * distinct web V-codes that one Shaka code covers. Returns `undefined`
   * for any other code, or when no media error code was captured.
   */
  public static getMediaErrorCode(error: IShakaErrorLike): number | undefined {
    const errorCode =
      typeof error.code === "number" ? error.code : Number(error.code);
    if (errorCode !== ShakaErrorCode.VIDEO_ERROR) return undefined;
    if (!Array.isArray(error.data) || error.data.length === 0) {
      return undefined;
    }
    const value = error.data[0];
    return typeof value === "number" ? value : undefined;
  }

  /**
   * Extract a 3-digit sub-error code from a Shaka error. For DRM license
   * errors with an actionable HTTP code, returns the HTTP code directly.
   * Otherwise returns `errorCode % 1000` zero-padded to 3 digits.
   *
   * Ported from kaveri `ShakaPlayerUtils.getSubErrorCode`.
   */
  public static getSubErrorCode(error: IShakaErrorLike): string {
    const httpCode = ShakaPlayerUtils.getHttpErrorCode(error);
    if (httpCode) return httpCode;
    const errorCode =
      typeof error.code === "number" ? error.code : Number(error.code);
    if (!Number.isFinite(errorCode)) return "000";
    return (errorCode % ERROR_CONVERSION_CONSTANT).toString().padStart(3, "0");
  }

  /**
   * Returns true when the error should be processed by the error handler.
   * LOAD_INTERRUPTED errors are suppressed — they occur naturally when a
   * new `load()` interrupts a pending one.
   *
   * Ported from kaveri `ShakaPlayerUtils.isErrorHandlingRequired`.
   */
  public static isErrorHandlingRequired(error: IShakaErrorLike): boolean {
    const errorCode =
      typeof error.code === "number" ? error.code : Number(error.code);
    return errorCode !== ShakaErrorCode.LOAD_INTERRUPTED;
  }

  /**
   * Determines whether the error is retryable. The classification is based
   * on the Shaka error category and specific error codes.
   *
   * Ported from kaveri `ShakaPlayerUtils.isRetryableError`.
   */
  public static isRetryableError(error: IShakaErrorLike): boolean {
    const errorCode =
      typeof error.code === "number" ? error.code : Number(error.code);
    const errorCategory =
      typeof error.category === "number"
        ? error.category
        : Number(error.category);

    switch (errorCategory) {
      case ShakaErrorCategory.ADS:
      case ShakaErrorCategory.CAST:
      case ShakaErrorCategory.MANIFEST:
      case ShakaErrorCategory.NETWORK:
      case ShakaErrorCategory.STORAGE:
      case ShakaErrorCategory.STREAMING:
        return false;
      case ShakaErrorCategory.DRM:
        if (ShakaPlayerUtils.getHttpErrorCode(error)) return false;
        if (
          errorCode === ShakaErrorCode.LICENSE_REQUEST_FAILED ||
          errorCode === ShakaErrorCode.LICENSE_RESPONSE_REJECTED
        ) {
          return true;
        }
        return false;
      case ShakaErrorCategory.MEDIA:
      case ShakaErrorCategory.TEXT:
        return true;
      case ShakaErrorCategory.PLAYER:
        if (
          errorCode === ShakaErrorCode.LOAD_INTERRUPTED ||
          errorCode === ShakaErrorCode.OPERATION_ABORTED ||
          errorCode === ShakaErrorCode.CONTENT_NOT_LOADED
        ) {
          return true;
        }
        return false;
      default:
        return true;
    }
  }

  /**
   * Returns true for the request failures Shaka retries a live stream on —
   * BAD_HTTP_STATUS, HTTP_ERROR and TIMEOUT. These are the codes Shaka's own
   * default `streaming.failureCallback` answers with `retryStreaming()`.
   */
  public static isLiveStreamingRetryError(error: IShakaErrorLike): boolean {
    const errorCode =
      typeof error.code === "number" ? error.code : Number(error.code);
    return (
      errorCode === ShakaErrorCode.BAD_HTTP_STATUS ||
      errorCode === ShakaErrorCode.HTTP_ERROR ||
      errorCode === ShakaErrorCode.TIMEOUT
    );
  }

  /**
   * Returns true when the error is a Shaka NETWORK category error.
   *
   * Ported from kaveri `ShakaPlayerUtils.isShakaNetworkError`.
   */
  public static isShakaNetworkError(error: IShakaErrorLike): boolean {
    const errorCategory =
      typeof error.category === "number"
        ? error.category
        : Number(error.category);
    return errorCategory === ShakaErrorCategory.NETWORK;
  }

  /**
   * Map a numeric (or numeric-string) Shaka error category to a human-readable
   * name (`"NETWORK"`, `"DRM"`, …). Unrecognised categories resolve to
   * `"UNKNOWN"`. This is what populates {@link TPlayerError.category} so the
   * emitted error is self-describing rather than a bare numeric string.
   */
  public static getCategoryName(category: number | string | undefined): string {
    const numeric = typeof category === "number" ? category : Number(category);
    switch (numeric) {
      case ShakaErrorCategory.NETWORK:
        return "NETWORK";
      case ShakaErrorCategory.TEXT:
        return "TEXT";
      case ShakaErrorCategory.MEDIA:
        return "MEDIA";
      case ShakaErrorCategory.MANIFEST:
        return "MANIFEST";
      case ShakaErrorCategory.STREAMING:
        return "STREAMING";
      case ShakaErrorCategory.DRM:
        return "DRM";
      case ShakaErrorCategory.PLAYER:
        return "PLAYER";
      case ShakaErrorCategory.CAST:
        return "CAST";
      case ShakaErrorCategory.STORAGE:
        return "STORAGE";
      case ShakaErrorCategory.ADS:
        return "ADS";
      default:
        return "UNKNOWN";
    }
  }

  // ── State mapping ──────────────────────────────────────────────────────────

  /**
   * Map a Shaka state-history entry's `state` string to {@link PlayerState}.
   * Falls back to {@link PlayerState.IDLE} for unrecognised values.
   */
  public static mapShakaState(state: string): PlayerState {
    const mapped = SHAKA_STATE_TO_PLAYER_STATE.get(state.toLowerCase());
    return mapped === undefined ? PlayerState.IDLE : mapped;
  }
}
